// Клиент «магии» — подбора вариантов поездки из библиотеки. Как и
// распознавание, ходит только к своему серверу: ключ провайдера в браузер не
// попадает, к провайдеру ходит сервер (server/ai/magic.mjs).
import { request } from '../../auth/request';
import type { ApiResult } from '../../auth/request';
import type { Currency, PriceScope, SegmentKind } from '../../types';

/**
 * Блок библиотеки в том виде, в каком он уходит модели. Это не `Segment`:
 * лишнего (заметок, id владельца, служебных полей) модели знать незачем, а
 * нужного в `Segment` нет — время в UTC. Считает его клиент: таблица
 * IATA → таймзона и Luxon живут здесь, на сервере их нет.
 */
export interface MagicSegmentInput {
  id: string;
  kind: SegmentKind;
  /** Человекочитаемая подпись — та же, что на карточке блока. */
  title: string;
  priceMinor: number;
  currency: Currency;
  from?: string;
  to?: string;
  carrier?: string;
  flightNumber?: string;
  departLocal?: string;
  arriveLocal?: string;
  /** Момент вылета/прилёта в миллисекундах UTC; null — таймзона неизвестна. */
  departUtc?: number | null;
  arriveUtc?: number | null;
  priceScope?: PriceScope;
  linkedTo?: string;
  durationMin?: number;
  city?: string;
  nights?: number;
  label?: string;
  quantity?: number;
  flightId?: string;
}

export interface MagicConfig {
  enabled: boolean;
  /** Включено, ключ есть и модель для магии выбрана — можно колдовать. */
  ready: boolean;
  model: string;
  maxSegments: number;
  maxVariants: number;
  maxWishesChars: number;
}

export interface MagicVariant {
  name: string;
  summary: string;
  segmentIds: string[];
  notes: string[];
  /** Дней между прилётом в точку назначения и обратным вылетом; null — не туда-обратно. */
  stayDays: number | null;
}

export interface MagicUsage {
  prompt: number;
  completion: number;
  total: number;
}

export interface MagicResult {
  ok: true;
  model: string;
  variants: MagicVariant[];
  notes: string[];
  usage?: MagicUsage;
}

export interface MagicInput {
  from: string;
  to: string;
  /** Язык интерфейса: на нём модель пишет названия и заметки вариантов. */
  lang: string;
  roundTrip: boolean;
  /** Диапазон пребывания в днях; имеет смысл только при roundTrip. */
  stayMinDays: number | null;
  stayMaxDays: number | null;
  maxVariants: number;
  wishes: string;
  travelers: number;
  currency: Currency;
  segments: MagicSegmentInput[];
}

/** Конфиг тянется один раз при открытии модала. 401 у гостя — штатный случай. */
export function fetchMagicConfig(signal?: AbortSignal): Promise<ApiResult<MagicConfig>> {
  return request<MagicConfig>('/api/magic/config', { method: 'GET', signal });
}

/** Сам подбор. Отменяемый: закрытие модала не должно оставлять висящий запрос. */
export function magicPlan(input: MagicInput, signal?: AbortSignal): Promise<ApiResult<MagicResult>> {
  return request<MagicResult>('/api/magic', { method: 'POST', json: input, signal });
}
